import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useWebSocket } from '../../hooks/useWebSocket';
import authService from '../../services/authService';
import API_CONFIG from '../../config/api';
import PopupAlert from './popup-alert';
import { buildPostShareChatMessage } from '../../utils/postShareChatMessage';

function PopupShare({
  display,
  onClose,
  postId,
  postTitle,
  shareUrl: shareUrlProp,
  contextLabel,
  formatShareMessage,
  formatShareSuccess,
}) {
  const location = useLocation();
  const { send, isConnected } = useWebSocket();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [, setFollowers] = useState([]);
  const [, setFollowing] = useState([]);
  const [combinedUsers, setCombinedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [postUrl, setPostUrl] = useState('');
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [shareSuccessMessage, setShareSuccessMessage] = useState('');

  // 현재 사용자 ID 가져오기
  const getCurrentUserId = () => {
    const data = authService.getUserData();
    return data.userId || data.id || null;
  };

  // 공유 URL 생성 및 팝업 열릴 때 검색어 초기화
  useEffect(() => {
    if (display !== 'none') {
      const currentUrl =
        shareUrlProp ??
        window.location.origin + location.pathname + location.search;
      setPostUrl(currentUrl);
      setSearchTerm(''); // 팝업이 열릴 때 검색어 초기화
    }
  }, [display, location, shareUrlProp]);

  // 팔로워/팔로잉 목록 조회
  useEffect(() => {
    if (display === 'none') {
      return;
    }

    const userId = getCurrentUserId();
    if (!userId) {
      setError('로그인이 필요합니다.');
      return;
    }

    setLoading(true);
    setError(null);

    // 팔로워와 팔로잉 목록을 동시에 가져오기
    Promise.all([
      fetch(`/api/followers?userId=${userId}`).then(res => res.json()),
      fetch(`/api/following?userId=${userId}`).then(res => res.json())
    ])
      .then(([followersData, followingData]) => {
        if (followersData.success) {
          setFollowers(followersData.followers || []);
        }
        if (followingData.success) {
          setFollowing(followingData.following || []);
        }
        
        // 두 목록을 합치고 중복 제거 (id 기준)
        const allUsers = [...(followersData.followers || []), ...(followingData.following || [])];
        const uniqueUsers = Array.from(
          new Map(allUsers.map(user => [user.id, user])).values()
        );
        setCombinedUsers(uniqueUsers);
      })
      .catch(() => {
        setError('목록을 불러오는데 실패했습니다.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [display]);

  // 검색어로 필터링
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(combinedUsers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = combinedUsers.filter(user => 
      user.nickname.toLowerCase().includes(term) ||
      (user.email && user.email.toLowerCase().includes(term))
    );
    setFilteredUsers(filtered);
  }, [searchTerm, combinedUsers]);

  const handleContentClick = (e) => {
    e.stopPropagation();
  };

  const recordShare = (platform) => {
    if (!postId) return;
    const token = authService.getUserData()?.token;
    fetch(`/api/post/${postId}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: 'include',
      body: JSON.stringify({ platform }),
    }).catch(() => {});
  };

  // 채팅으로 공유
  const handleShareToChat = async (user) => {
    if (!isConnected) {
      setShareSuccessMessage('채팅 서버에 연결되지 않았습니다.');
      setShowShareSuccess(true);
      return;
    }

    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      setShareSuccessMessage('로그인이 필요합니다.');
      setShowShareSuccess(true);
      return;
    }
    if (user?.id == null) {
      setShareSuccessMessage('상대 사용자 정보를 찾을 수 없습니다.');
      setShowShareSuccess(true);
      return;
    }

    try {
      let groupId;
      let content;

      if (postId) {
        // 게시글 공유: 백엔드 API가 포스트 메타 + 그룹 ID를 한 번에 반환
        const res = await fetch('/api/post-share', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ postId, targetUserId: user.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          setShareSuccessMessage(data.message ?? '공유에 실패했습니다.');
          setShowShareSuccess(true);
          return;
        }
        groupId = data.groupId;
        content = buildPostShareChatMessage(data.post);
        recordShare('chat');
      } else {
        // 프로필 공유 등 URL 기반 공유 (기존 흐름 유지)
        const dmToken = authService.getUserData().token;
        const res = await fetch(
          `/api/chat/private-dm-group?userId=${encodeURIComponent(String(currentUserId))}&targetUserId=${encodeURIComponent(String(user.id))}`,
          {
            credentials: 'include',
            headers: dmToken ? { Authorization: `Bearer ${dmToken}` } : {}
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success || data.groupId == null) {
          setShareSuccessMessage(data.message ?? '채팅방을 준비하지 못했습니다.');
          setShowShareSuccess(true);
          return;
        }
        groupId = data.groupId;
        content = formatShareMessage ? formatShareMessage(postUrl) : postUrl;
      }

      send({ type: 'update', content, group: groupId });
      setShareSuccessMessage(
        formatShareSuccess
          ? formatShareSuccess(user)
          : `${user.nickname}님에게 공유했습니다.`
      );
      setShowShareSuccess(true);
    } catch {
      setShareSuccessMessage('공유에 실패했습니다.');
      setShowShareSuccess(true);
    }
  };

  // 링크 복사
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      recordShare('link');
      setShowCopySuccess(true);
    } catch (error) {
      console.error('Copy failed:', error);
      setShareSuccessMessage('링크 복사에 실패했습니다.');
      setShowShareSuccess(true);
    }
  };

  const handleCopySuccessConfirm = () => {
    setShowCopySuccess(false);
  };

  const handleShareSuccessConfirm = () => {
    setShowShareSuccess(false);
  };

  return (
    <>
      <div
        className="box-col"
        style={{
          width: '100%', height: '100%',
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          display: display, justifyContent: 'center', alignItems: 'center',
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
        }}
        onClick={onClose}
      >
        <div
          className="card-col gap10 popup-content"
          style={{ 
            maxWidth: '500px',
            maxHeight: '80%', 
            padding: 20, 
          }}
          onClick={handleContentClick}
        >
          <div className="box-row group-align-std">
            <h3>공유</h3>
            <button 
              onClick={onClose}
              style={{ fontSize: '24px', cursor: 'pointer' }}
            >
              ×
            </button>
          </div>

          {contextLabel ? (
            <p style={{ margin: '0 0 8px', fontSize: 14, color: '#666', lineHeight: 1.4 }}>
              {contextLabel}
            </p>
          ) : null}

          {/* 검색 입력창 */}
          <div className="box-col pd0">
            <input
              type="text"
              placeholder="사용자 검색 (닉네임 또는 이메일)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 40px 10px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#999',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* 사용자 목록 */}
          <div style={{ 
            width:'100%', minHeight: '200px', maxHeight: '300px', 
            padding: '10px',
            overflowY: 'auto',
            border: '1px solid #eee', borderRadius: '4px'
          }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <p>로딩 중...</p>
              </div>
            )}
            {error && (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p style={{ color: 'red' }}>{error}</p>
              </div>
            )}
            {!loading && !error && filteredUsers.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <span className="material-symbols-rounded" style={{ fontSize: '48px', marginBottom: '10px', display: 'block', opacity: 0.5 }}>
                  {searchTerm ? 'search_off' : 'person_off'}
                </span>
                <p>{searchTerm ? '검색 결과가 없습니다.' : '공유할 수 있는 사용자가 없습니다.'}</p>
              </div>
            )}
            {!loading && !error && filteredUsers.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {filteredUsers.map(user => (
                  <li
                    key={user.id}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: 12, 
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      backgroundColor: 'transparent'
                    }}
                    onClick={() => handleShareToChat(user)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <img
                      className='img-profile-comment'
                      src={user.profile_image ? API_CONFIG.resolveFileUrl(user.profile_image) : API_CONFIG.getDefaultAvatar(user.id)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = API_CONFIG.getDefaultAvatar(user.id);
                      }}
                      alt="profile"
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold' }}>{user.nickname}</div>
                      {user.profile_intro && (
                        <div style={{ fontSize: '0.85em', color: '#555' }}>{user.profile_intro}</div>
                      )}
                    </div>
                    <span className="material-symbols-rounded" style={{ color: '#666' }}>
                      send
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* 링크 복사 영역 */}
          <div style={{
            width: '100%', 
            padding: '15px', 
            backgroundColor: '#f9f9f9', 
            border: '1px solid #eee', borderRadius: '4px'
          }}>
            <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>링크 복사</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={postUrl}
                readOnly
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '12px',
                  backgroundColor: 'white'
                }}
              />
              <button
                onClick={handleCopyLink}
                className="btn-rtg btn-monotone-1"
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
              >
                복사
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 복사 성공 팝업 */}
      {showCopySuccess && (
        <PopupAlert
          message="링크가 클립보드에 복사되었습니다."
          onConfirm={handleCopySuccessConfirm}
        />
      )}

      {/* 공유 성공 팝업 */}
      {showShareSuccess && (
        <PopupAlert
          message={shareSuccessMessage}
          onConfirm={handleShareSuccessConfirm}
        />
      )}
    </>
  );
}

export default PopupShare;

